import React, { memo, useCallback, useEffect, useMemo, useRef } from "react";
import i18n from "@microdoing/shared/i18n";
import { getUserId } from "@microdoing/shared/services/user/selectors";
import {
  addLikeToCommentary,
  removeLikeFromCommentary,
} from "@microdoing/shared/services/userCampaign/actions";
import ArrowReplyIcon from "app/components/Icons/ArrowReplyIcon";
import LikeIcon from "app/components/Icons/Like/LikeIcon";
import AudioMessage from "app/scenes/MessageScreen/ChatScreen/AudioMessage";
import theme from "app/Theme/Theme";
import normalizeSource from "app/utils/normalizeSource";
import { StyleSheet } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "../Box";
import { MainText } from "../MainText";
import { VideoModal } from "../index";
import { ImageModal } from "../index";
import { CommentaryType } from "./Commentary";
import FillLikeIcon from "app/components/Icons/Like/FillLikeIcon";
import { PressableOpacity } from "../PressableOpacity";
import normalize from "app/utils/normalize";
import useTagUser from "app/hooks/useTagUser";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";

export interface ICommentaryContentProps {
  commentary: CommentaryType;
  commentaryWidth: number;
  handleResponse: ({
    stepCommentId,
    isModifier,
    comment,
    receiver,
  }) => () => void;
}

const styles = StyleSheet.create({
  reply: {
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
  },
});

const CommentaryContent = ({
  commentary,
  handleResponse,
  commentaryWidth,
}: ICommentaryContentProps) => {
  const fileContainerRef = useRef(null);

  const userId = useSelector(getUserId);

  const commentIsLike = useMemo(
    () => commentary?.like?.users?.some(el => el === userId),
    [userId, commentary?.like?.users],
  );

  const commentaryComment = commentary?.comment;
  const file = commentary?.fileId || {};
  const isFile = !!commentary?.fileId;
  const isVideo = file?.fileType?.includes("video");
  const isImage = file?.fileType?.includes("image");

  const dispatch = useDispatch();
  const [
    textFormatWithColor,
    textFormatInString,
    addTagUser,
    formatTextColor,
    formatTaggedUserBackToFront,
    formatTextFromToBack,
    resetAllTag,
  ] = useTagUser({ isNotFromInput: true });

  useEffect(() => {
    if (commentaryComment) {
      formatTaggedUserBackToFront(commentaryComment);
    }
  }, [commentaryComment]);

  const handlePressLike = useCallback(() => {
    if (commentIsLike) {
      dispatch(
        removeLikeFromCommentary({
          stepCommentId: commentary._id,
          userId,
        }),
      );
    } else {
      dispatch(addLikeToCommentary({ stepCommentId: commentary._id, userId }));
    }
  }, [
    commentIsLike,
    commentary._id,
    userId,
    addLikeToCommentary,
    removeLikeFromCommentary,
  ]);

  const previewStyle = useMemo(() => {
    return {
      // width: commentaryWidth,
      borderRadius: 5,
    };
  }, [commentaryWidth]);

  const source = useMemo(() => {
    return {
      uri: normalizeSource(file?.fileUrl),
    };
  }, [file?.fileUrl]);

  const imagePreview = useMemo(() => {
    return normalizeSource(file?.filePreview);
  }, [file?.filePreview]);

  return (
    <Box width="100%" ref={fileContainerRef}>
      {!!commentary.comment && (
        <MainText
          paddingTop={0}
          padding={0}
          variant="l4-medium"
          color="neutral.900"
        >
          {isWebDesktop ? textFormatInString : textFormatWithColor}
        </MainText>
      )}
      {isFile && (
        <Box
          flex={1}
          width="100%"
          justifyContent="center"
          alignItems="center"
          marginTop="xs"
        >
          {isVideo ? (
            <VideoModal
              maxHeight={isWebDesktop ? 250 : null}
              commentaryWidth={commentaryWidth}
              fileUrl={file?.fileUrl}
              imagePreview={imagePreview}
              source={source}
              video={file?.fileUrl}
            />
          ) : isImage ? (
            <ImageModal
              maxHeight={isWebDesktop ? 250 : null}
              modalImageStyle={isWebDesktop ? previewStyle : null}
              source={source}
            />
          ) : (
            <Box width="100%">
              <AudioMessage soundUrl={file.fileUrl} hasDarkBackground={false} />
            </Box>
          )}
        </Box>
      )}
      <Box
        flexDirection="row"
        marginTop="xs"
        justifyContent="flex-start"
        alignItems="center"
      >
        <PressableOpacity onPress={handlePressLike} style={styles.reply}>
          {!!commentIsLike ? (
            <FillLikeIcon
              width={normalize(16)}
              height={normalize(16)}
              color={theme.colors["brand.500"]}
            />
          ) : (
            <LikeIcon
              width={normalize(16)}
              height={normalize(16)}
              color={theme.colors["neutral.600"]}
            />
          )}
          <MainText
            variant="l3-bold"
            marginLeft="xs"
            marginRight="xs"
            color={commentIsLike ? "brand.500" : "neutral.600"}
          >
            {commentary?.like?.numberOfLike}
          </MainText>
        </PressableOpacity>
        <PressableOpacity
          onPress={handleResponse({
            stepCommentId: !commentary.commentId
              ? commentary._id
              : commentary.commentId,
            isModifier: false,
            receiver: commentary.user,
            comment: commentary.comment,
          })}
          style={styles.reply}
        >
          <ArrowReplyIcon width={normalize(16)} height={normalize(16)} />
          <MainText variant="l3-bold" marginLeft="xs" color="neutral.600">
            {i18n.t("commentary-reply")}
          </MainText>
        </PressableOpacity>
      </Box>
    </Box>
  );
};

export default memo(CommentaryContent);
